/**
 * Panel principal de la Empresa (Carlos jun 28, 2026 — Etapa 2)
 *
 * 4 tabs:
 *   1. Inicio — KPIs + accesos rápidos
 *   2. Nueva transferencia — quote + form destinatario → confirmar
 *   3. Historial — lista de envíos + link a comprobante
 *   4. Perfil — datos empresa + estado, link a docs si pendiente
 *
 * Acceso solo con empresa_token. Si la empresa no está APPROVED, redirige a /empresas/docs.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import empresaApi from '../utils/empresaApi';
import CopyableField from '../components/CopyableField';
import { BRAND } from '../brand';

// Cuenta bancaria oficial — se refresca desde GET /empresas/me/deposit-accounts.
// El fallback local solo aporta la razón social/RUT/email de la marca (que
// están en el preset). Los datos bancarios (banco, número, tipo) se dejan
// vacíos: se llenan cuando la API los devuelve, y si no vienen la UI muestra
// el estado "cuentas no configuradas" en vez de datos de otra marca.
const PLATFORM_BANK = {
  titular: BRAND.legalName || '',
  rut: BRAND.legalRut || '',
  banco: '',
  tipoCuenta: '',
  numeroCuenta: '',
  email: BRAND.supportEmail || '',
};

// Cuenta {BRAND.name} en BOLIVIA (Banco Económico) — para envíos Bolivia→Chile.
// Fallback local; se refresca desde GET /empresas/me/deposit-accounts.
const PLATFORM_BANK_BO_FALLBACK = {
  titular: 'Carlos Alberto Silva Heredia',
  ci: '6340130',
  banco: 'Banco Económico',
  tipoCuenta: 'Cuenta Ahorro',
  numeroCuenta: '1011447346',
  email: `${BRAND.supportEmail}`,
};

const BANCOS_CHILE_FALLBACK = [
  'BancoEstado (Cuenta RUT)', 'Banco de Chile', 'Banco Santander', 'Banco BCI',
  'Scotiabank', 'Banco Itaú', 'Banco Falabella', 'Banco Ripley', 'Banco Security',
  'Banco BICE', 'Banco Consorcio', 'Coopeuch', 'Mercado Pago (Cuenta Vista)', 'Tenpo', 'MACH',
];

const TIPOS_CUENTA_CHILE = [
  { value: 'cuenta_rut', label: 'Cuenta RUT (BancoEstado)' },
  { value: 'corriente', label: 'Cuenta Corriente' },
  { value: 'vista', label: 'Cuenta Vista' },
  { value: 'ahorro', label: 'Cuenta de Ahorro' },
];

// === Banderas (igual estilo que CalculatorLuxury home) ===
const FLAG_URLS = { CL: 'https://flagcdn.com/w80/cl.png', BO: 'https://flagcdn.com/w80/bo.png' };
const FLAG_EMOJIS = { CL: '🇨🇱', BO: '🇧🇴' };
const FlagImg = ({ country, size = 24 }) => {
  const [err, setErr] = useState(false);
  if (err || !FLAG_URLS[country]) {
    return <span style={{ fontSize: size * 0.9, lineHeight: 1 }} role="img" aria-label={country}>{FLAG_EMOJIS[country]}</span>;
  }
  return (
    <img src={FLAG_URLS[country]} alt={country} onError={() => setErr(true)}
      style={{ width: size, height: 'auto', borderRadius: 2, verticalAlign: 'middle' }} loading="eager" />
  );
};

const formatMiles = (num) => {
  if (num === '' || num === null || num === undefined) return '';
  const n = parseFloat(String(num).replace(/\./g, '').replace(',', '.'));
  if (isNaN(n)) return '';
  return Math.round(n).toLocaleString('es-CL', { timeZone: 'America/Santiago' });
};
const unformat = (s) => (s || '').toString().replace(/\./g, '').replace(/[^\d]/g, '');

// Carlos jul 6, 2026 — fechas SIEMPRE en hora chilena, formato día/mes/año 24h
const fmtFechaCl = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const p = new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
  } catch { return iso.slice(0, 16).replace('T', ' '); }
};

const TABS = [
  { key: 'inicio', icon: '🏠', label: 'Inicio' },
  { key: 'nueva', icon: '💸', label: 'Nueva transferencia' },
  { key: 'historial', icon: '📋', label: 'Historial' },
  { key: 'perfil', icon: '🏢', label: 'Perfil' },
];

const EmpresaPanel = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('inicio');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const r = await empresaApi.get('/empresas/me');
      setMe(r.data);
      if (r.data.status !== 'approved') {
        navigate('/empresas/docs');
      }
    } catch {
      navigate('/empresas/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('empresa_token')) {
      navigate('/empresas/login');
      return;
    }
    loadMe();
  }, [loadMe, navigate]);

  const logout = () => {
    localStorage.removeItem('empresa_token');
    navigate('/empresas/login');
  };

  if (loading || !me) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <p className="font-bold text-slate-900 truncate">🏢 {me.razon_social}</p>
            <p className="text-[10px] text-slate-500">{me.rut} · {me.email}</p>
          </div>
          <button onClick={logout} className="text-xs text-slate-500 hover:text-slate-700" data-testid="emp-panel-logout">
            Salir
          </button>
        </div>
        {/* Tabs */}
        <nav className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto" data-testid="emp-panel-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`emp-tab-${t.key}`}
              className={`px-3 py-2 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap ${
                tab === t.key
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-5">
        {tab === 'inicio' && <TabInicio onGoNueva={() => setTab('nueva')} onGoHistorial={() => setTab('historial')} />}
        {tab === 'nueva' && <TabNueva onCreated={() => setTab('historial')} />}
        {tab === 'historial' && <TabHistorial />}
        {tab === 'perfil' && <TabPerfil me={me} />}
      </main>
    </div>
  );
};

// =============================================================
// Tab Inicio
// =============================================================
const TabInicio = ({ onGoNueva, onGoHistorial }) => {
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState(null);

  useEffect(() => {
    empresaApi.get('/empresas/me/stats').then((r) => setStats(r.data));
    // Carlos jul 3, 2026 — ganancias del partner (BOB→CLP) por cobrar
    empresaApi.get('/empresas/me/partner-commissions').then((r) => setCommissions(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-500">Cargando KPIs…</div>;

  const month = stats.month || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Envíos este mes" value={month.count ?? 0} icon="📦" />
        <Kpi label="Volumen mes (CLP)" value={`$${(month.total_clp ?? 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`} icon="💸" />
        <Kpi label="Entregado mes (BOB)" value={`${(month.total_bob ?? 0).toLocaleString('es-BO')}`} icon="🇧🇴" />
      </div>

      {/* 💰 Ganancias acumuladas del partner (envíos Bolivia→Chile) */}
      {commissions && (commissions.pending_clp > 0 || commissions.paid_clp > 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="emp-partner-commissions">
          <p className="text-sm font-bold text-emerald-900">💰 Tus ganancias (envíos Bolivia→Chile)</p>
          <div className="flex flex-wrap gap-4 mt-1.5">
            <div>
              <p className="text-xl font-black text-emerald-700" data-testid="emp-commission-pending">
                ${(commissions.pending_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP
              </p>
              <p className="text-[11px] text-emerald-700">Por cobrar ({commissions.pending_count} envíos) — {BRAND.name} te lo abona</p>
            </div>
            {commissions.paid_clp > 0 && (
              <div>
                <p className="text-xl font-black text-slate-500">${(commissions.paid_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP</p>
                <p className="text-[11px] text-slate-500">Ya pagado ✓</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          onClick={onGoNueva}
          data-testid="emp-cta-nueva"
          className="bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-2xl text-left px-5 shadow-md transition"
        >
          <p className="text-2xl font-black">💸 Nueva transferencia</p>
          <p className="text-sm opacity-90 mt-1">Cotiza y envía a Bolivia con tu comisión empresa</p>
        </button>
        <button
          onClick={onGoHistorial}
          data-testid="emp-cta-historial"
          className="bg-white border-2 border-slate-200 hover:border-emerald-400 py-6 rounded-2xl text-left px-5 shadow-sm transition"
        >
          <p className="text-2xl font-black text-slate-900">📋 Ver historial</p>
          <p className="text-sm text-slate-600 mt-1">Tus {stats.total_count_all_time} envíos · descargá comprobantes</p>
        </button>
      </div>

      {stats.last_order && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
          <p className="font-semibold text-blue-900">📦 Última operación</p>
          <p className="text-xs text-blue-800">
            <code>{stats.last_order.short_no}</code> ·
            ${stats.last_order.amount_clp?.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP →
            {Math.round(stats.last_order.amount_bob || 0).toLocaleString('es-BO')} BOB ·
            <span className="ml-1 px-1.5 py-0.5 bg-blue-200 rounded text-[10px] font-semibold">
              {stats.last_order.status}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
    <p className="text-xs text-slate-500">{icon} {label}</p>
    <p className="text-xl font-black text-slate-900 mt-0.5">{value}</p>
  </div>
);

// =============================================================
// Tab Nueva transferencia (Carlos jun 28, 2026 — flujo completo)
// =============================================================
const BANCOS_FALLBACK = [
  'Banco Mercantil Santa Cruz', 'Banco Nacional de Bolivia (BNB)', 'Banco BCP Bolivia',
  'Banco Bisa', 'Banco Unión', 'Banco Económico', 'Banco Solidario (BancoSol)',
  'Banco Ganadero', 'Banco FIE', 'Banco Fortaleza', 'Banco Prodem', 'Banco Fassil',
  'Tigo Money (billetera)',
];

const TabNueva = ({ onCreated }) => {
  const [step, setStep] = useState(1); // 1: monto 2: recipient (+QR) 3: confirm+comprobante
  // Carlos jul 2, 2026 — dirección del envío: clp_bob (Chile→Bolivia) | bob_clp (Bolivia→Chile)
  const [direction, setDirection] = useState('clp_bob');
  const directionRef = useRef('clp_bob');
  // Inputs en formato display (con miles "100.000"). Calculadora estilo home (auto)
  const [displayClp, setDisplayClp] = useState('100.000');
  const [displayBob, setDisplayBob] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastEditedRef = useRef('clp'); // 'clp' | 'bob'
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const [bancos, setBancos] = useState(BANCOS_FALLBACK);
  const [bancosChile, setBancosChile] = useState(BANCOS_CHILE_FALLBACK);
  const [depositBO, setDepositBO] = useState(PLATFORM_BANK_BO_FALLBACK);
  const [metodoPago, setMetodoPago] = useState('transferencia'); // cómo paga la empresa en Bolivia: transferencia | qr_bolivia
  const [rec, setRec] = useState({ nombre: '', banco: '', cuenta: '', tipo_cuenta: 'caja_ahorro', ci: '', rut: '', telefono: '' });
  const [proposito, setProposito] = useState('apoyo_familiar');
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [comprobantePreview, setComprobantePreview] = useState(null);
  const [bankDataCopied, setBankDataCopied] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState(null); // id si vino de libreta
  const [saveRecipient, setSaveRecipient] = useState(false);     // toggle "guardar para próx"
  const [submittedOrder, setSubmittedOrder] = useState(null);    // datos de la orden creada para mostrar success

  // === Carlos jul 2, 2026 — Pago al destinatario con QR (igual que clientes normales) ===
  const [payMethod, setPayMethod] = useState('bank');   // 'bank' | 'qr'
  const [qrPreview, setQrPreview] = useState(null);     // data URL de la imagen QR
  const [qrAnalysis, setQrAnalysis] = useState(null);   // resultado IA {banco, titular, cuenta, vencimiento_qr, is_expired}
  const [qrAnalyzing, setQrAnalyzing] = useState(false);

  // === Carlos jul 3, 2026 — CALCULADORA UNIFICADA con margen del partner ===
  // El partner escribe lo que COBRA a su cliente (bruto). El sistema descuenta
  // su % (config admin ⚙️, default 1.4%) y cotiza/crea la orden con el NETO,
  // que es lo que transfiere a {BRAND.name}. Si el admin pone 0%, bruto = neto y
  // la calculadora funciona directa (empresas que no revenden).
  const resellPctRef = useRef(1.4);
  const [resellPctLoaded, setResellPctLoaded] = useState(false);

  useEffect(() => {
    empresaApi.get('/empresas/me/resell-config').then((r) => {
      if (typeof r.data?.resell_pct === 'number') resellPctRef.current = r.data.resell_pct;
    }).catch(() => {}).finally(() => setResellPctLoaded(true));
  }, []);

  const toNeto = (bruto) => {
    const pct = resellPctRef.current || 0;
    return Math.round(bruto * (1 - pct / 100));
  };
  const fromNeto = (neto) => {
    const pct = resellPctRef.current || 0;
    return pct > 0 ? Math.ceil(neto / (1 - pct / 100)) : neto;
  };

  const loadSavedRecipients = useCallback(() => {
    empresaApi.get('/empresas/me/recipients').then((r) => {
      setSavedRecipients(r.data?.items || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    empresaApi.get('/empresas/me/bancos-bolivia').then((r) => {
      if (r.data?.bancos?.length) setBancos(r.data.bancos);
    }).catch(() => {});
    empresaApi.get('/empresas/me/bancos-chile').then((r) => {
      if (r.data?.bancos?.length) setBancosChile(r.data.bancos);
    }).catch(() => {});
    empresaApi.get('/empresas/me/deposit-accounts').then((r) => {
      const bo = r.data?.BOB;
      if (bo) {
        setDepositBO({
          titular: bo.account_holder || PLATFORM_BANK_BO_FALLBACK.titular,
          ci: bo.ci || PLATFORM_BANK_BO_FALLBACK.ci,
          banco: bo.bank || PLATFORM_BANK_BO_FALLBACK.banco,
          tipoCuenta: bo.account_type || PLATFORM_BANK_BO_FALLBACK.tipoCuenta,
          numeroCuenta: bo.account_number || PLATFORM_BANK_BO_FALLBACK.numeroCuenta,
          email: bo.email || PLATFORM_BANK_BO_FALLBACK.email,
        });
      }
    }).catch(() => {});
    loadSavedRecipients();
  }, [loadSavedRecipients]);

  const pickSavedRecipient = (item) => {
    setRec({
      nombre: item.nombre || '',
      banco: item.banco || '',
      cuenta: item.cuenta || '',
      tipo_cuenta: item.tipo_cuenta || (directionRef.current === 'bob_clp' ? 'cuenta_rut' : 'caja_ahorro'),
      ci: item.ci || '',
      rut: item.rut || '',
      telefono: item.telefono || '',
    });
    setSelectedSavedId(item.id);
    setSaveRecipient(false); // ya está guardado
  };

  const clearSavedSelection = () => {
    setSelectedSavedId(null);
    setRec({ nombre: '', banco: '', cuenta: '', tipo_cuenta: directionRef.current === 'bob_clp' ? 'cuenta_rut' : 'caja_ahorro', ci: '', rut: '', telefono: '' });
  };

  const deleteSavedRecipient = async (id, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!window.confirm('¿Eliminar este destinatario de tu libreta?')) return;
    try {
      await empresaApi.delete(`/empresas/me/recipients/${id}`);
      if (selectedSavedId === id) clearSavedSelection();
      loadSavedRecipients();
    } catch (err) { /* noop */ }
  };

  const runQuote = useCallback(async (body, side, reqId) => {
    try {
      const r = await empresaApi.post('/empresas/me/quote', { ...body, direction: directionRef.current });
      // Ignorar respuesta obsoleta (otra petición ya se disparó)
      if (reqId !== reqIdRef.current) return;
      setQuote(r.data);
      // Carlos jul 3, 2026 — el campo ORIGEN muestra el BRUTO (lo que cobra el
      // partner a su cliente); el backend cotiza con el NETO. Si el user editó
      // el DESTINO (reverso), el origen a mostrar = fromNeto(neto del backend).
      const originSide = directionRef.current === 'bob_clp' ? 'bob' : 'clp';
      if (side === 'clp') {
        // editó CLP → seteamos BOB
        setDisplayBob(formatMiles(originSide === 'bob' ? fromNeto(r.data.amount_bob) : r.data.amount_bob));
      } else {
        // editó BOB → seteamos CLP
        setDisplayClp(formatMiles(originSide === 'clp' ? fromNeto(r.data.amount_clp) : r.data.amount_clp));
      }
      setError('');
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setError(e?.response?.data?.detail || 'No se pudo cotizar');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  // Auto-calc al tipear (debounced 350ms)
  const triggerAutoQuote = useCallback((side, rawValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const n = parseInt(unformat(rawValue), 10);
    reqIdRef.current += 1;
    const myReqId = reqIdRef.current;
    if (!n || n <= 0) {
      setQuote(null);
      setLoading(false);
      if (side === 'clp') setDisplayBob('');
      else setDisplayClp('');
      return;
    }
    setLoading(true);
    // Si el user editó el campo ORIGEN, lo tipeado es BRUTO → cotizar el NETO.
    const originSide = directionRef.current === 'bob_clp' ? 'bob' : 'clp';
    const sendVal = side === originSide ? toNeto(n) : n;
    debounceRef.current = setTimeout(() => {
      runQuote(side === 'clp' ? { amount_clp: sendVal } : { amount_bob: sendVal }, side, myReqId);
    }, 350);
  }, [runQuote]);

  // Disparar cálculo inicial al montar (CLP 100.000) — espera a cargar el %
  useEffect(() => {
    if (resellPctLoaded) triggerAutoQuote('clp', '100.000');
  }, [triggerAutoQuote, resellPctLoaded]);

  const handleClpChange = (raw) => {
    const num = unformat(raw);
    lastEditedRef.current = 'clp';
    setDisplayClp(formatMiles(num));
    triggerAutoQuote('clp', num);
  };
  const handleBobChange = (raw) => {
    const num = unformat(raw);
    lastEditedRef.current = 'bob';
    setDisplayBob(formatMiles(num));
    triggerAutoQuote('bob', num);
  };

  // Carlos jul 2, 2026 — cambiar dirección del envío resetea calculadora y destinatario
  const switchDirection = (d) => {
    if (d === direction) return;
    directionRef.current = d;
    setDirection(d);
    setError('');
    setQuote(null);
    setSelectedSavedId(null);
    setSaveRecipient(false);
    setPayMethod('bank');
    setQrPreview(null); setQrAnalysis(null);
    setMetodoPago('transferencia');
    setRec({ nombre: '', banco: '', cuenta: '', tipo_cuenta: d === 'bob_clp' ? 'cuenta_rut' : 'caja_ahorro', ci: '', rut: '', telefono: '' });
    if (d === 'bob_clp') {
      setDisplayBob('1.000'); setDisplayClp('');
      triggerAutoQuote('bob', '1.000');
    } else {
      setDisplayClp('100.000'); setDisplayBob('');
      triggerAutoQuote('clp', '100.000');
    }
  };

  const netoOrigin = () => toNeto(parseInt(unformat(direction === 'bob_clp' ? displayBob : displayClp), 10) || 0);
  const brutoOrigin = () => parseInt(unformat(direction === 'bob_clp' ? displayBob : displayClp), 10) || 0;
  // Ganancia del partner en CLP (solo BOB→CLP): (bruto − neto) × tasa de la orden
  const gananciaClp = () => Math.round((brutoOrigin() - netoOrigin()) * (quote?.effective_rate || 0));
  // Carlos jul 3, 2026 — tasa que el partner OFRECE a su cliente: destino / bruto
  // (coherente con las 2 cifras visibles en pantalla; la tasa interna va sobre el neto).
  const clientRate = () => {
    const bruto = brutoOrigin();
    if (!bruto) return 0;
    const dest = parseInt(unformat(direction === 'bob_clp' ? displayClp : displayBob), 10) || 0;
    return dest / bruto;
  };

  const goToStep2 = () => {
    setError('');
    if (direction === 'bob_clp') {
      const bobN = parseInt(unformat(displayBob), 10);
      if (!quote || !bobN || bobN < 100) {
        setError('Ingresa un monto válido (mínimo Bs 100)');
        return;
      }
    } else {
      const clpN = parseInt(unformat(displayClp), 10);
      if (!quote || !clpN || clpN < 5000) {
        setError('Ingresa un monto válido (mínimo $5.000 CLP)');
        return;
      }
    }
    setStep(2);
  };

  const fileToB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => {
      const v = r.result || '';
      const i = v.indexOf(',');
      res(i >= 0 ? v.slice(i + 1) : v);
    };
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const handleCompFile = async (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { setError('Comprobante demasiado grande (máx 6MB)'); return; }
    setComprobanteFile(f);
    setComprobantePreview(URL.createObjectURL(f));
  };

  // Sube y analiza el QR del destinatario con IA (mismo flujo que clientes normales)
  const handleQrFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError('El QR es demasiado grande (máx 5MB)'); return; }
    setError('');
    setQrAnalysis(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      setQrPreview(reader.result);
      setQrAnalyzing(true);
      empresaApi.post('/empresas/me/qr/analyze', { image_base64: reader.result })
        .then((r) => {
          if (r.data?.success && r.data?.data) {
            const a = r.data.data;
            setQrAnalysis(a);
            // Autocompletar datos del destinatario desde la IA
            setRec((prev) => ({
              ...prev,
              nombre: prev.nombre || a.titular || '',
              banco: a.banco || prev.banco,
              cuenta: a.cuenta || prev.cuenta,
            }));
          } else {
            setQrAnalysis({ no_data: true });
          }
        })
        .catch(() => setQrAnalysis({ no_data: true }))
        .finally(() => setQrAnalyzing(false));
    };
    reader.readAsDataURL(f);
  };

  const clearQr = () => { setQrPreview(null); setQrAnalysis(null); };

  const submit = async () => {
    setError('');
    if (!rec.nombre) {
      setError('Completá el nombre del destinatario');
      return;
    }
    if (direction === 'bob_clp') {
      if (!rec.rut) { setError('El RUT del destinatario es obligatorio para pagar en Chile'); return; }
      if (!rec.banco || !rec.cuenta) { setError('Completá banco y número de cuenta del destinatario'); return; }
    } else if (payMethod === 'qr') {
      if (!qrPreview) { setError('Subí el código QR de pago del destinatario'); return; }
      if (qrAnalysis?.is_expired) { setError('El código QR está vencido. Subí un QR vigente.'); return; }
    } else if (!rec.banco || !rec.cuenta) {
      setError('Completá los datos del destinatario (nombre, banco, cuenta)');
      return;
    }
    if (!comprobanteFile) {
      setError('Adjuntá el comprobante de tu depósito para confirmar el envío');
      return;
    }
    // Carlos jul 3, 2026 — CLP→BOB: la orden se crea con el NETO (el partner
    // recauda y transfiere su neto). BOB→CLP: el cliente FINAL paga el BRUTO
    // directo a la cuenta {BRAND.name} → se envía el BRUTO y el backend registra
    // la comisión del partner (pagadera en CLP).
    const clpN = toNeto(parseInt(unformat(displayClp), 10) || 0);
    const bobN = parseInt(unformat(displayBob), 10) || 0;
    setLoading(true);
    try {
      const comprobante_b64 = await fileToB64(comprobanteFile);

      // Payload del destinatario según método (QR incluye imagen + análisis IA)
      const recipientPayload = { ...rec, payment_method: direction === 'bob_clp' ? 'bank' : payMethod };
      if (direction !== 'bob_clp' && payMethod === 'qr' && qrPreview) {
        const i = qrPreview.indexOf(',');
        recipientPayload.qr_b64 = i >= 0 ? qrPreview.slice(i + 1) : qrPreview;
        recipientPayload.qr_mime = (qrPreview.match(/^data:([^;]+);/) || [])[1] || 'image/jpeg';
        recipientPayload.qr_data = qrAnalysis && !qrAnalysis.no_data ? qrAnalysis : null;
      }

      const resp = await empresaApi.post('/empresas/me/order/create', {
        direction,
        ...(direction === 'bob_clp' ? { amount_bob: bobN, metodo_pago: metodoPago } : { amount_clp: clpN }),
        // Carlos jul 3, 2026 — monto destino que la empresa VIO al cotizar.
        // El backend lo honra si su recotización difiere ≤0.7% (fix "104→103").
        quoted_amount_dest: direction === 'bob_clp' ? quote?.amount_clp : quote?.amount_bob,
        recipient: recipientPayload,
        proposito,
        comprobante_b64,
        comprobante_mime: comprobanteFile.type || 'image/jpeg',
        // Carlos jul 2, 2026 — el QR NO se guarda en la libreta (suele vencer)
        save_recipient: payMethod === 'qr' ? false : saveRecipient,
        saved_recipient_id: selectedSavedId,
      });
      // Capturar datos de la orden creada para pantalla de éxito
      setSubmittedOrder({
        short_no: resp.data?.short_no,
        order_id: resp.data?.order_id,
        direction,
        amount_clp: direction === 'bob_clp' ? quote?.amount_clp : clpN,
        amount_bob: direction === 'bob_clp' ? bobN : quote?.amount_bob,
        recipient_name: rec.nombre,
        recipient_bank: payMethod === 'qr' ? (qrAnalysis?.banco || 'Pago con QR 📱') : rec.banco,
        status: resp.data?.status,
      });
      loadSavedRecipients(); // por si se guardó uno nuevo
      setStep(4); // pantalla success
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo crear la orden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7">
      <h2 className="text-xl font-black text-slate-900 mb-1">
        {direction === 'bob_clp' ? '💸 Nueva transferencia a Chile 🇨🇱' : '💸 Nueva transferencia a Bolivia 🇧🇴'}
      </h2>
      <p className="text-xs text-slate-500 mb-4">{step === 4 ? '¡Envío realizado!' : `Paso ${step} de 3`}</p>

      {step === 1 && (
        <div className="space-y-4" data-testid="emp-step1-calc">
          {/* === Selector de dirección (Carlos jul 2, 2026) === */}
          <div className="grid grid-cols-2 gap-2" data-testid="emp-direction-toggle">
            <button
              type="button"
              onClick={() => switchDirection('clp_bob')}
              data-testid="emp-direction-clp-bob"
              className={`py-3 px-2 rounded-xl border-2 text-sm font-bold transition flex items-center justify-center gap-1.5 ${
                direction === 'clp_bob'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
              }`}
            >
              <FlagImg country="CL" size={18} /> → <FlagImg country="BO" size={18} />
              <span className="ml-1">Chile a Bolivia</span>
            </button>
            <button
              type="button"
              onClick={() => switchDirection('bob_clp')}
              data-testid="emp-direction-bob-clp"
              className={`py-3 px-2 rounded-xl border-2 text-sm font-bold transition flex items-center justify-center gap-1.5 ${
                direction === 'bob_clp'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
              }`}
            >
              <FlagImg country="BO" size={18} /> → <FlagImg country="CL" size={18} />
              <span className="ml-1">Bolivia a Chile</span>
            </button>
          </div>

          {/* === Calculadora estilo {BRAND.name} home (verde + azul, auto-calc) === */}
          <div className="bg-white rounded-2xl border-4 shadow-lg p-5 sm:p-6" style={{ borderColor: '#008000' }}>
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-2">💰</span>
              <h3 className="text-lg font-bold" style={{ color: '#362777' }}>Calcula tu envío</h3>
            </div>

            {direction === 'bob_clp' ? (
              <>
                {/* Monto a enviar (BOB) — origen Bolivia */}
                <label className="block mb-3">
                  <span className="block text-xs font-bold text-gray-700 mb-1">{resellPctRef.current > 0 ? 'Cobras a tu cliente (Bs):' : 'Monto a enviar (Bs):'}</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold" style={{ color: '#008000' }}>Bs</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={displayBob}
                      onChange={(e) => handleBobChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      data-testid="emp-input-bob"
                      className="w-full pl-14 pr-24 py-4 text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2"
                      style={{ borderColor: '#008000' }}
                      placeholder="1.000"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <span className="font-bold text-gray-600 text-sm">BOB</span>
                      <FlagImg country="BO" size={22} />
                    </div>
                  </div>
                </label>

                <div className="text-center my-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                    <FlagImg country="CL" size={20} />
                    <span className="text-xs font-semibold text-emerald-700">Envío a Chile 🇨🇱</span>
                  </div>
                </div>

                {/* Monto a recibir (CLP) */}
                <label className="block mb-2">
                  <span className="block text-xs font-bold text-gray-700 mb-1">Destinatario recibe en Chile:</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold" style={{ color: '#3B82F6' }}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={displayClp}
                      onChange={(e) => handleClpChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      data-testid="emp-input-clp"
                      className="w-full pl-12 pr-16 py-4 text-2xl font-bold border-2 rounded-xl bg-blue-50 focus:outline-none focus:ring-2 focus:bg-white"
                      style={{ borderColor: '#3B82F6' }}
                      placeholder="0"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <FlagImg country="CL" size={26} />
                    </div>
                  </div>
                </label>

                <p className="text-center text-[11px] text-gray-500 mt-2" data-testid="emp-rate-line">
                  {loading
                    ? 'Calculando…'
                    : quote
                      ? (resellPctRef.current > 0
                          ? `🏷️ Tasa para tu cliente: 1 BOB = ${clientRate().toFixed(2)} CLP`
                          : `Tasa: 1 BOB = ${Number(quote.effective_rate || 0).toFixed(2)} CLP`)
                      : 'Ingresa un monto para cotizar'}
                </p>
                {quote && !loading && resellPctRef.current > 0 && (
                  <div className="text-center mt-1 space-y-0.5" data-testid="emp-neto-line">
                    <p className="text-xs font-bold text-indigo-700">
                      📥 Tu cliente transfiere a {BRAND.name}: Bs {brutoOrigin().toLocaleString('es-BO')}
                    </p>
                    <p className="text-xs font-bold text-emerald-700" data-testid="emp-ganancia-line">
                      💰 Tu ganancia: ${gananciaClp().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP ({BRAND.name} te la abona)
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Monto a enviar (CLP) */}
                <label className="block mb-3">
                  <span className="block text-xs font-bold text-gray-700 mb-1">{resellPctRef.current > 0 ? 'Cobras a tu cliente (CLP):' : 'Monto a enviar (CLP):'}</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold" style={{ color: '#008000' }}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={displayClp}
                      onChange={(e) => handleClpChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      data-testid="emp-input-clp"
                      className="w-full pl-12 pr-24 py-4 text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2"
                      style={{ borderColor: '#008000' }}
                      placeholder="100.000"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <span className="font-bold text-gray-600 text-sm">CLP</span>
                      <FlagImg country="CL" size={22} />
                    </div>
                  </div>
                </label>

                {/* Destino fijo Bolivia (visual coherente con home) */}
                <div className="text-center my-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                    <FlagImg country="BO" size={20} />
                    <span className="text-xs font-semibold text-emerald-700">Envío a Bolivia 🇧🇴</span>
                  </div>
                </div>

                {/* Monto a recibir (BOB) */}
                <label className="block mb-2">
                  <span className="block text-xs font-bold text-gray-700 mb-1">Destinatario recibe en Bolivia:</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold" style={{ color: '#3B82F6' }}>Bs</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={displayBob}
                      onChange={(e) => handleBobChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      data-testid="emp-input-bob"
                      className="w-full pl-14 pr-16 py-4 text-2xl font-bold border-2 rounded-xl bg-blue-50 focus:outline-none focus:ring-2 focus:bg-white"
                      style={{ borderColor: '#3B82F6' }}
                      placeholder="0"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <FlagImg country="BO" size={26} />
                    </div>
                  </div>
                </label>

                <p className="text-center text-[11px] text-gray-500 mt-2" data-testid="emp-rate-line">
                  {loading
                    ? 'Calculando…'
                    : quote
                      ? (resellPctRef.current > 0
                          ? `🏷️ Tasa para tu cliente: 1 CLP = ${clientRate().toFixed(6)} BOB`
                          : `Tasa: 1 CLP = ${quote.effective_rate?.toFixed(6)} BOB`)
                      : 'Ingresa un monto para cotizar'}
                </p>
                {quote && !loading && resellPctRef.current > 0 && (
                  <div className="text-center mt-1 space-y-0.5" data-testid="emp-neto-line">
                    <p className="text-xs font-bold text-indigo-700">
                      💸 Tú transfieres a {BRAND.name}: ${netoOrigin().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP
                    </p>
                    <p className="text-xs font-bold text-emerald-700" data-testid="emp-ganancia-line-clp">
                      💰 Tu ganancia: ${(brutoOrigin() - netoOrigin()).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP (te la quedas tú)
                    </p>
                  </div>
                )}
              </>
            )}
          </div>


          {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3">{error}</div>}

          <button
            onClick={goToStep2}
            disabled={!quote || loading}
            data-testid="emp-quote-continue"
            className="w-full text-white font-bold py-4 rounded-xl text-lg transition transform hover:scale-[1.02] disabled:opacity-60 disabled:transform-none flex items-center justify-center gap-2"
            style={{ backgroundColor: '#008000' }}
          >
            <span className="text-xl">🚀</span>
            Continuar con el envío
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          {/* === Selector método de pago al destinatario (solo Chile→Bolivia) === */}
          {direction === 'clp_bob' && (
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1.5">¿Cómo recibe tu destinatario? *</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setPayMethod('bank'); setError(''); }}
                data-testid="emp-paymethod-bank"
                className={`py-3 px-2 rounded-xl border-2 text-sm font-bold transition ${
                  payMethod === 'bank'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                }`}
              >
                🏦 Cuenta bancaria
              </button>
              <button
                type="button"
                onClick={() => { setPayMethod('qr'); setSelectedSavedId(null); setSaveRecipient(false); setError(''); }}
                data-testid="emp-paymethod-qr"
                className={`py-3 px-2 rounded-xl border-2 text-sm font-bold transition ${
                  payMethod === 'qr'
                    ? 'border-purple-600 bg-purple-50 text-purple-800'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                }`}
              >
                📱 QR de pago
              </button>
            </div>
          </div>
          )}

          {/* === Libreta de destinatarios guardados (filtrada por país destino) === */}
          {(direction === 'bob_clp' || payMethod === 'bank') && (() => {
            const paisDestino = direction === 'bob_clp' ? 'Chile' : 'Bolivia';
            const filteredSaved = savedRecipients.filter((s) =>
              paisDestino === 'Chile' ? s.pais === 'Chile' : (s.pais || 'Bolivia') === 'Bolivia'
            );
            return filteredSaved.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="emp-saved-recipients-block">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-emerald-900">📚 Tus destinatarios en {paisDestino} ({filteredSaved.length})</p>
                {selectedSavedId && (
                  <button onClick={clearSavedSelection} className="text-[10px] text-slate-500 hover:text-slate-700" data-testid="emp-clear-saved">
                    ✕ Limpiar
                  </button>
                )}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {filteredSaved.map((s) => {
                  const active = selectedSavedId === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => pickSavedRecipient(s)}
                      data-testid={`emp-saved-recipient-${s.id}`}
                      className={`relative min-w-[160px] text-left px-3 py-2 rounded-lg border-2 transition shadow-sm ${
                        active ? 'border-emerald-600 bg-white' : 'border-emerald-200 bg-white hover:border-emerald-400'
                      }`}
                    >
                      <p className="text-[11px] font-bold text-slate-900 truncate">
                        {s.alias || s.nombre}
                      </p>
                      {s.alias && <p className="text-[10px] text-slate-500 truncate">{s.nombre}</p>}
                      <p className="text-[10px] text-slate-500 truncate">{s.banco}</p>
                      <p className="text-[10px] text-slate-400">··· {s.cuenta.slice(-4)}</p>
                      {s.usage_count > 0 && (
                        <span className="absolute top-1 right-1 text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold">
                          {s.usage_count}×
                        </span>
                      )}
                      <span
                        onClick={(e) => deleteSavedRecipient(s.id, e)}
                        className="absolute bottom-1 right-1 text-[10px] text-slate-300 hover:text-red-500 cursor-pointer px-1"
                        title="Eliminar"
                        data-testid={`emp-delete-saved-${s.id}`}
                      >
                        🗑
                      </span>
                    </button>
                  );
                })}
              </div>
              {selectedSavedId && (
                <p className="text-[10px] text-emerald-700 mt-2 font-semibold">
                  ✓ Usando destinatario guardado. Los campos se autocompletaron.
                </p>
              )}
            </div>
            );
          })()}

          <Field label="Nombre completo del destinatario *" value={rec.nombre} onChange={(v) => { setRec({ ...rec, nombre: v }); setSelectedSavedId(null); }} testid="emp-rec-nombre" />

          {direction === 'bob_clp' && (
            <>
              <Field label="RUT del destinatario *" value={rec.rut} onChange={(v) => { setRec({ ...rec, rut: v }); setSelectedSavedId(null); }} placeholder="12.345.678-9" testid="emp-rec-rut" />
              <label className="block">
                <span className="text-xs font-semibold text-slate-700 mb-1 block">Banco en Chile *</span>
                <select value={rec.banco} onChange={(e) => { setRec({ ...rec, banco: e.target.value }); setSelectedSavedId(null); }} data-testid="emp-rec-banco-chile"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="">-- Selecciona un banco --</option>
                  {bancosChile.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700 mb-1 block">Tipo de cuenta *</span>
                <select value={rec.tipo_cuenta} onChange={(e) => setRec({ ...rec, tipo_cuenta: e.target.value })} data-testid="emp-rec-tipo-chile"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  {TIPOS_CUENTA_CHILE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <Field label="Número de cuenta *" value={rec.cuenta} onChange={(v) => { setRec({ ...rec, cuenta: v }); setSelectedSavedId(null); }} testid="emp-rec-cuenta" />
            </>
          )}

          {direction === 'clp_bob' && payMethod === 'bank' && (
            <>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700 mb-1 block">Banco en Bolivia *</span>
                <select value={rec.banco} onChange={(e) => { setRec({ ...rec, banco: e.target.value }); setSelectedSavedId(null); }} data-testid="emp-rec-banco"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="">-- Selecciona un banco --</option>
                  {bancos.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <Field label="Número de cuenta *" value={rec.cuenta} onChange={(v) => { setRec({ ...rec, cuenta: v }); setSelectedSavedId(null); }} testid="emp-rec-cuenta" />
              <label className="block">
                <span className="text-xs font-semibold text-slate-700 mb-1 block">Tipo de cuenta *</span>
                <select value={rec.tipo_cuenta} onChange={(e) => setRec({ ...rec, tipo_cuenta: e.target.value })} data-testid="emp-rec-tipo"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="caja_ahorro">Caja de ahorro</option>
                  <option value="corriente">Corriente</option>
                </select>
              </label>
            </>
          )}

          {/* === Bloque QR de pago (Carlos jul 2, 2026 — igual que clientes normales) === */}
          {direction === 'clp_bob' && payMethod === 'qr' && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="emp-qr-block">
              <p className="text-sm font-bold text-purple-900 mb-1">📱 Código QR de pago del destinatario</p>
              <p className="text-[11px] text-purple-700 mb-3">
                Subí el QR bancario boliviano. Nuestra IA lee el banco, titular y cuenta automáticamente, y verifica que no esté vencido.
              </p>

              {!qrPreview ? (
                <div
                  className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center cursor-pointer bg-white hover:bg-purple-50/50 transition"
                  onClick={() => {
                    const inp = document.getElementById('emp-qr-input');
                    if (inp) { inp.value = ''; inp.click(); }
                  }}
                  data-testid="emp-qr-upload-block"
                >
                  <div className="text-4xl mb-2">📤</div>
                  <p className="text-gray-600 font-medium text-sm">Toca para subir el QR</p>
                  <p className="text-xs text-gray-400 mt-1">PNG o JPG hasta 5MB</p>
                </div>
              ) : (
                <div className="text-center">
                  <img src={qrPreview} alt="QR de pago" className="max-h-48 mx-auto rounded-lg border border-purple-200 bg-white p-1" data-testid="emp-qr-preview" />
                  <button type="button" onClick={clearQr} className="mt-2 text-xs text-purple-700 underline" data-testid="emp-qr-change">
                    ✕ Cambiar QR
                  </button>
                </div>
              )}
              <input
                id="emp-qr-input"
                type="file"
                accept="image/*"
                data-testid="emp-qr-input"
                onChange={(e) => handleQrFile(e.target.files?.[0])}
                style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}
              />

              {qrAnalyzing && (
                <div className="mt-3 flex items-center gap-2 text-xs text-purple-700 font-medium" data-testid="emp-qr-analyzing">
                  <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-purple-600 border-t-transparent rounded-full" />
                  Analizando QR con IA…
                </div>
              )}

              {qrAnalysis && !qrAnalyzing && (
                <div className="mt-3 space-y-2">
                  {qrAnalysis.is_expired && (
                    <div className="p-3 bg-red-100 border-2 border-red-400 rounded-lg text-xs" data-testid="emp-qr-expired">
                      <p className="font-bold text-red-700">⛔ QR VENCIDO</p>
                      <p className="text-red-600">{qrAnalysis.expiration_warning || 'Subí un QR vigente para continuar.'}</p>
                    </div>
                  )}
                  {qrAnalysis.expiration_warning && !qrAnalysis.is_expired && (
                    <div className="p-2.5 bg-yellow-50 border border-yellow-300 rounded-lg text-xs text-yellow-800 font-medium" data-testid="emp-qr-expiry-warning">
                      ⚠️ {qrAnalysis.expiration_warning}
                    </div>
                  )}
                  {qrAnalysis.no_data && (
                    <div className="p-2.5 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-600" data-testid="emp-qr-nodata">
                      No pudimos leer los datos del QR automáticamente. Igual puedes continuar — completa el nombre del destinatario arriba.
                    </div>
                  )}
                  {!qrAnalysis.no_data && (qrAnalysis.banco || qrAnalysis.titular || qrAnalysis.cuenta) && (
                    <div className="p-3 bg-white border border-purple-200 rounded-lg text-xs space-y-1" data-testid="emp-qr-analysis-data">
                      <p className="font-bold text-purple-900 mb-1">✓ Datos leídos del QR:</p>
                      {qrAnalysis.titular && <p><span className="text-slate-500">Titular:</span> <span className="font-semibold">{qrAnalysis.titular}</span></p>}
                      {qrAnalysis.banco && <p><span className="text-slate-500">Banco:</span> <span className="font-semibold">{qrAnalysis.banco}</span></p>}
                      {qrAnalysis.cuenta && <p><span className="text-slate-500">Cuenta:</span> <span className="font-semibold">{qrAnalysis.cuenta}</span></p>}
                      {qrAnalysis.vencimiento_qr && !qrAnalysis.is_expired && <p><span className="text-slate-500">Vence:</span> <span className="font-medium">{qrAnalysis.vencimiento_qr}</span></p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {direction === 'clp_bob' && (
            <Field label="CI del destinatario (opcional)" value={rec.ci} onChange={(v) => setRec({ ...rec, ci: v })} testid="emp-rec-ci" />
          )}
          <Field label="Teléfono (opcional)" value={rec.telefono} onChange={(v) => setRec({ ...rec, telefono: v })} testid="emp-rec-tel" />

          <label className="block">
            <span className="text-xs font-semibold text-slate-700 mb-1 block">Propósito</span>
            <select value={proposito} onChange={(e) => setProposito(e.target.value)} data-testid="emp-proposito"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
              <option value="apoyo_familiar">Apoyo familiar</option>
              <option value="pago_servicios">Pago de servicios</option>
              <option value="pago_proveedor">Pago a proveedor</option>
              <option value="comercial">Comercial</option>
              <option value="otro">Otro</option>
            </select>
          </label>

          {/* === Toggle guardar destinatario (no aplica a QR — suelen vencer) === */}
          {payMethod === 'bank' && !selectedSavedId && (
            <label className="flex items-center gap-2 cursor-pointer bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="emp-save-recipient-toggle">
              <input
                type="checkbox"
                checked={saveRecipient}
                onChange={(e) => setSaveRecipient(e.target.checked)}
                className="w-4 h-4 accent-emerald-600"
                data-testid="emp-save-recipient-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">💾 Guardar este destinatario</p>
                <p className="text-[11px] text-slate-500">Lo vas a poder reusar en próximos envíos con 1 click.</p>
              </div>
            </label>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3">{error}</div>}

          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(1)} className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm" data-testid="emp-step2-back">← Atrás</button>
            <button
              onClick={() => {
                setError('');
                if (!rec.nombre) { setError('Completá el nombre del destinatario'); return; }
                if (direction === 'bob_clp') {
                  if (!rec.rut) { setError('El RUT del destinatario es obligatorio para pagar en Chile'); return; }
                  if (!rec.banco || !rec.cuenta) { setError('Completá banco y número de cuenta del destinatario'); return; }
                } else if (payMethod === 'qr') {
                  if (!qrPreview) { setError('Subí el código QR de pago del destinatario'); return; }
                  if (qrAnalysis?.is_expired) { setError('El código QR está vencido. Subí un QR vigente.'); return; }
                } else if (!rec.banco || !rec.cuenta) {
                  setError('Completá banco y número de cuenta del destinatario');
                  return;
                }
                setStep(3);
              }}
              className="flex-1 px-3 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg"
              data-testid="emp-step2-continue"
            >Continuar →</button>
          </div>
        </div>
      )}

      {step === 3 && quote && (
        <div className="space-y-4">
          {/* === Banner instrucciones === */}
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm">
            <p className="font-bold text-amber-900">📌 Paso final — depositá y sube el comprobante</p>
            <p className="text-xs text-amber-800 mt-1">
              {direction === 'bob_clp'
                ? `Tu cliente (o tú) transfiere Bs ${brutoOrigin().toLocaleString('es-BO')} a la cuenta de ${BRAND.name} en Bolivia (${depositBO.banco}). Subí el comprobante — nuestra IA lo verifica al instante.`
                : `Transfiere $${netoOrigin().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP a la cuenta de ${BRAND.name} (${BRAND.legalName}) y adjuntá el comprobante. Nuestra IA lo verifica al instante.`}
            </p>
          </div>

          {/* === Resumen partner: tasa cliente + ganancia + monto a {BRAND.name} destacado (Carlos jul 3, 2026) === */}
          {resellPctRef.current > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-4 border-2 border-emerald-300" data-testid="emp-step3-partner-summary">
              <p className="text-sm font-black text-slate-800 mb-2">💼 Tu resumen de esta operación</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between" data-testid="emp-step3-client-rate">
                  <span className="text-slate-500">🏷️ Tasa que le das a tu cliente:</span>
                  <span className="font-bold text-slate-800">
                    {direction === 'bob_clp'
                      ? `1 BOB = ${clientRate().toFixed(2)} CLP`
                      : `1 CLP = ${clientRate().toFixed(6)} BOB`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">📥 Cobras a tu cliente:</span>
                  <span className="font-bold text-slate-800">
                    {direction === 'bob_clp'
                      ? `Bs ${brutoOrigin().toLocaleString('es-BO')}`
                      : `$${brutoOrigin().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`}
                  </span>
                </div>
                <div className="flex justify-between" data-testid="emp-step3-ganancia">
                  <span className="text-emerald-700 font-semibold">💰 Tu ganancia:</span>
                  <span className="font-black text-emerald-700">
                    {direction === 'bob_clp'
                      ? `$${gananciaClp().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP (${BRAND.name} te la abona)`
                      : `$${(brutoOrigin() - netoOrigin()).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP (te la quedas tú)`}
                  </span>
                </div>
              </div>
              <div className="mt-3 bg-emerald-600 rounded-lg py-2.5 px-3 text-center" data-testid={`emp-step3-pay-${BRAND.name.toLowerCase()}`}>
                <p className="text-[11px] font-semibold text-emerald-100">
                  {direction === 'bob_clp' ? `🏦 Tu cliente deposita a ${BRAND.name}:` : `🏦 Tú pagas a ${BRAND.name}:`}
                </p>
                <p className="text-2xl font-black text-white">
                  {direction === 'bob_clp'
                    ? `Bs ${brutoOrigin().toLocaleString('es-BO')}`
                    : `$${netoOrigin().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`}
                </p>
              </div>
            </div>
          )}

          {direction === 'bob_clp' ? (
            /* === Pago en BOLIVIA: transferencia o QR Banco Económico (Carlos jul 2, 2026) === */
            <div className="bg-white rounded-xl shadow-lg p-5 border border-slate-200" data-testid="emp-bank-data-card-bo">
              <h3 className="text-lg font-bold mb-3" style={{ color: '#008000' }}>
                🇧🇴 Paga en Bolivia
              </h3>

              {/* Toggle método de pago */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setMetodoPago('transferencia')}
                  data-testid="emp-metodo-transferencia"
                  className={`py-2.5 px-2 rounded-xl border-2 text-sm font-bold transition ${
                    metodoPago === 'transferencia'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  🏦 Transferencia
                </button>
                <button
                  type="button"
                  onClick={() => setMetodoPago('qr_bolivia')}
                  data-testid="emp-metodo-qr-bolivia"
                  className={`py-2.5 px-2 rounded-xl border-2 text-sm font-bold transition ${
                    metodoPago === 'qr_bolivia'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  }`}
                >
                  📱 Pago con QR
                </button>
              </div>

              {metodoPago === 'qr_bolivia' ? (
                <div className="text-center" data-testid="emp-qr-banco-economico">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Escanea este QR desde tu app bancaria boliviana:</p>
                  <img
                    src="/images/qr_banco_economico.png"
                    alt="QR Banco Económico"
                    className="max-w-[240px] w-full mx-auto rounded-xl border-2 border-emerald-200 shadow-sm"
                  />
                  <p className="text-xs text-slate-500 mt-2">{depositBO.banco} · {depositBO.titular}</p>
                  <p className="text-sm font-black text-emerald-700 mt-1" data-testid="emp-monto-bs-qr">
                    Monto: Bs {brutoOrigin().toLocaleString('es-BO')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <CopyableField label="Titular" value={depositBO.titular} testId="emp-bank-bo-titular" />
                    <CopyableField label="CI" value={depositBO.ci} testId="emp-bank-bo-ci" />
                    <CopyableField label="Banco" value={depositBO.banco} testId="emp-bank-bo-banco" />
                    <CopyableField label="Tipo de Cuenta" value={depositBO.tipoCuenta} testId="emp-bank-bo-tipo" />
                    <CopyableField label="N° de Cuenta" value={depositBO.numeroCuenta} highlight testId="emp-bank-bo-cuenta" />
                    <CopyableField label="Monto a transferir" value={`Bs ${brutoOrigin().toLocaleString('es-BO')}`} highlight copyValue={String(brutoOrigin())} testId="emp-bank-bo-monto" />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const monto = brutoOrigin();
                      const txt =
                        `📋 Datos para Transferencia — ${BRAND.name} Bolivia\n\n` +
                        `Titular: ${depositBO.titular}\n` +
                        `CI: ${depositBO.ci}\n` +
                        `Banco: ${depositBO.banco}\n` +
                        `Tipo de Cuenta: ${depositBO.tipoCuenta}\n` +
                        `N° de Cuenta: ${depositBO.numeroCuenta}\n\n` +
                        `💰 Monto a transferir: Bs ${monto.toLocaleString('es-BO')}`;
                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(txt);
                        } else {
                          const ta = document.createElement('textarea');
                          ta.value = txt;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.focus(); ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                        }
                        setBankDataCopied(true);
                        setTimeout(() => setBankDataCopied(false), 2000);
                      } catch (err) { console.error('Copy failed:', err); }
                    }}
                    className={`mt-3 w-full py-2.5 rounded-lg font-bold transition flex items-center justify-center gap-2 shadow-sm ${
                      bankDataCopied
                        ? 'bg-green-100 text-green-800 border-2 border-green-400'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                    }`}
                    data-testid="emp-copy-all-bank-bo-btn"
                  >
                    {bankDataCopied ? '✅ ¡Datos copiados!' : '📋 Copiar TODOS los datos'}
                  </button>
                </>
              )}
            </div>
          ) : (
          /* === Datos para transferencia — estilo idéntico a flujo usuario === */
          <div className="bg-white rounded-xl shadow-lg p-5 border border-slate-200" data-testid="emp-bank-data-card">
            <h3 className="text-lg font-bold mb-3" style={{ color: '#008000' }}>
              🏦 Datos para Transferencia (Chile)
            </h3>
            <div className="space-y-1">
              <CopyableField label="Titular" value={PLATFORM_BANK.titular} testId="emp-bank-titular" />
              <CopyableField label="RUT" value={PLATFORM_BANK.rut} testId="emp-bank-rut" />
              <CopyableField label="Banco" value={PLATFORM_BANK.banco} testId="emp-bank-banco" />
              <CopyableField label="Tipo de Cuenta" value={PLATFORM_BANK.tipoCuenta} testId="emp-bank-tipo" />
              <CopyableField label="N° de Cuenta" value={PLATFORM_BANK.numeroCuenta} highlight testId="emp-bank-cuenta" />
              <CopyableField label="Email" value={PLATFORM_BANK.email} testId="emp-bank-email" />
              <CopyableField label="Monto a transferir" value={`$${netoOrigin().toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`} highlight copyValue={String(netoOrigin())} testId="emp-bank-monto" />
            </div>

            {/* Botón copiar TODO */}
            <button
              type="button"
              onClick={async () => {
                const monto = netoOrigin();
                const txt =
                  `📋 Datos para Transferencia — ${BRAND.name}\n\n` +
                  `Titular: ${PLATFORM_BANK.titular}\n` +
                  `RUT: ${PLATFORM_BANK.rut}\n` +
                  `Banco: ${PLATFORM_BANK.banco}\n` +
                  `Tipo de Cuenta: ${PLATFORM_BANK.tipoCuenta}\n` +
                  `N° de Cuenta: ${PLATFORM_BANK.numeroCuenta}\n` +
                  `Email: ${PLATFORM_BANK.email}\n\n` +
                  `💰 Monto a transferir: $${monto.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`;
                try {
                  if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(txt);
                  } else {
                    const ta = document.createElement('textarea');
                    ta.value = txt;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.focus(); ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                  }
                  setBankDataCopied(true);
                  setTimeout(() => setBankDataCopied(false), 2000);
                } catch (err) { console.error('Copy failed:', err); }
              }}
              className={`mt-3 w-full py-2.5 rounded-lg font-bold transition flex items-center justify-center gap-2 shadow-sm ${
                bankDataCopied
                  ? 'bg-green-100 text-green-800 border-2 border-green-400'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
              }`}
              data-testid="emp-copy-all-bank-btn"
            >
              {bankDataCopied ? '✅ ¡Datos copiados!' : '📋 Copiar TODOS los datos'}
            </button>
          </div>
          )}

          {/* === Resumen del destinatario === */}
          <div className="bg-slate-50 rounded-xl p-3 text-sm">
            <p className="font-semibold text-slate-700 mb-1">👤 Destinatario en {direction === 'bob_clp' ? 'Chile' : 'Bolivia'}</p>
            <p className="text-xs text-slate-700">{rec.nombre}</p>
            {direction === 'bob_clp' ? (
              <>
                {rec.rut && <p className="text-xs text-slate-600">RUT: {rec.rut}</p>}
                <p className="text-xs text-slate-600">{rec.banco} · {rec.cuenta} ({(TIPOS_CUENTA_CHILE.find((t) => t.value === rec.tipo_cuenta) || {}).label || rec.tipo_cuenta})</p>
              </>
            ) : payMethod === 'qr' ? (
              <div className="flex items-center gap-2 mt-1" data-testid="emp-confirm-qr-badge">
                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-[11px] font-bold border border-purple-200">📱 Pago con QR</span>
                {qrAnalysis?.banco && <span className="text-xs text-slate-600">{qrAnalysis.banco}</span>}
                {qrPreview && <img src={qrPreview} alt="QR" className="w-10 h-10 rounded border border-purple-200 object-cover ml-auto" />}
              </div>
            ) : (
              <p className="text-xs text-slate-600">{rec.banco} · {rec.cuenta} ({rec.tipo_cuenta === 'caja_ahorro' ? 'Caja de ahorro' : 'Corriente'})</p>
            )}
            {rec.ci && <p className="text-xs text-slate-600">CI: {rec.ci}</p>}
            <p className="text-xs text-emerald-700 font-bold mt-1">
              Recibe: {direction === 'bob_clp'
                ? `$${Math.round(quote.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`
                : `${Math.round(quote.amount_bob || 0).toLocaleString('es-BO')} BOB`}
            </p>
          </div>

          {/* === Comprobante — estilo idéntico a flujo usuario === */}
          <div className="bg-white rounded-xl shadow-lg p-5 border border-slate-200">
            <h3 className="text-lg font-bold mb-1" style={{ color: '#008000' }}>
              Último paso — muestrános que ya pagaste
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Sacá una foto clara de tu comprobante. Nuestra IA lo lee en segundos y activa tu envío.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <p className="font-semibold text-blue-800 text-sm mb-1">📱 ¿Cómo subir tu comprobante correctamente?</p>
              <ul className="text-xs text-blue-700 space-y-0.5">
                <li>✅ Sube un <strong>screenshot</strong> directo de tu app bancaria</li>
                <li>✅ Que se vea claramente: monto, fecha, número de transacción</li>
                <li>❌ NO tomes foto con otro celular a la pantalla</li>
                <li>❌ NO envíes imágenes borrosas o recortadas</li>
              </ul>
            </div>

            <div
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-emerald-50/30 transition"
              style={{ borderColor: '#008000' }}
              onClick={() => {
                const inp = document.getElementById('emp-comprobante-input');
                if (inp) { inp.value = ''; inp.click(); }
              }}
              data-testid="emp-comprobante-block"
            >
              <input
                id="emp-comprobante-input"
                type="file"
                accept="image/*,application/pdf"
                data-testid="emp-comprobante-input"
                onChange={(e) => handleCompFile(e.target.files?.[0])}
                style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}
              />
              {comprobantePreview ? (
                <div>
                  <img src={comprobantePreview} alt="Comprobante" className="max-h-40 mx-auto rounded-lg mb-2" />
                  <p className="text-green-600 text-sm font-semibold">✓ Imagen cargada</p>
                  <p className="text-xs text-gray-500 mt-1">Toca para cambiar</p>
                </div>
              ) : (
                <div>
                  <div className="text-4xl mb-2">📄</div>
                  <p className="text-gray-600 font-medium">Toca para subir tu comprobante</p>
                  <p className="text-xs text-gray-400 mt-1">Screenshot de tu app bancaria</p>
                </div>
              )}
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3">{error}</div>}

          <div className="flex gap-2">
            <button onClick={() => setStep(2)} className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm" data-testid="emp-step3-back">← Atrás</button>
            <button
              onClick={submit} disabled={loading || !comprobanteFile}
              data-testid="emp-step3-submit"
              className="flex-[2] py-3 font-bold rounded-lg text-white transition flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: comprobanteFile && !loading ? '#008000' : '#9ca3af' }}
            >
              {loading ? 'Enviando…' : '✓ ¡Listo! Ya pagué'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && submittedOrder && (
        <div className="space-y-4 text-center py-6" data-testid="emp-success-screen">
          {/* Check verde grande */}
          <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center shadow-lg">
            <span className="text-6xl">✅</span>
          </div>
          <div>
            <h2 className="text-2xl font-black text-emerald-700">¡Envío realizado con éxito!</h2>
            <p className="text-sm text-slate-600 mt-1">Estamos procesando tu transferencia 💚</p>
          </div>

          {/* Card resumen */}
          <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-md p-5 text-left max-w-md mx-auto" data-testid="emp-success-summary">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
              <span className="text-xs font-semibold text-slate-500">N° de orden</span>
              <code className="text-sm font-bold text-emerald-700" data-testid="emp-success-short-no">{submittedOrder.short_no}</code>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Enviaste:</span><span className="font-bold text-slate-900">{submittedOrder.direction === 'bob_clp' ? `Bs ${(submittedOrder.amount_bob || 0).toLocaleString('es-BO')}` : `$${(submittedOrder.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP`}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Recibe:</span><span className="font-bold text-emerald-700">{submittedOrder.direction === 'bob_clp' ? `$${Math.round(submittedOrder.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP` : `${Math.round(submittedOrder.amount_bob || 0).toLocaleString('es-BO')} BOB`}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Destinatario:</span><span className="font-semibold text-slate-900">{submittedOrder.recipient_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Banco:</span><span className="text-slate-700 text-right text-xs">{submittedOrder.recipient_bank}</span></div>
              <div className="flex justify-between pt-1.5 mt-1.5 border-t border-slate-100">
                <span className="text-slate-500">Estado:</span>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-bold">
                  📤 Verificando comprobante
                </span>
              </div>
            </div>
          </div>

          {/* Pasos siguientes */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left max-w-md mx-auto text-sm">
            <p className="font-bold text-blue-900 mb-2">📋 ¿Qué sigue?</p>
            <ol className="space-y-1.5 text-blue-800 text-xs list-decimal pl-4">
              <li>Nuestro equipo verifica tu comprobante (toma minutos en horario hábil).</li>
              {submittedOrder.direction === 'bob_clp' ? (
                <li>Procesamos el envío en Chile y subimos el comprobante de pago al destinatario.</li>
              ) : (
                <>
                  <li>Procesamos el envío en Bolivia y subimos el comprobante de pago al destinatario.</li>
                  <li>Al completarse, vas a recibir tu <strong>boleta electrónica DTE</strong> a nombre de tu empresa.</li>
                </>
              )}
              <li>Podés seguir el estado en tu pestaña <strong>Historial</strong> 📋</li>
            </ol>
          </div>

          {/* Acciones */}
          <div className="flex gap-2 max-w-md mx-auto pt-2">
            <button
              onClick={() => {
                // Hacer otro envío: reset todo (mantiene la dirección elegida)
                setQuote(null);
                setRec({ nombre: '', banco: '', cuenta: '', tipo_cuenta: direction === 'bob_clp' ? 'cuenta_rut' : 'caja_ahorro', ci: '', rut: '', telefono: '' });
                setComprobanteFile(null); setComprobantePreview(null);
                setSelectedSavedId(null); setSaveRecipient(false);
                setPayMethod('bank'); setQrPreview(null); setQrAnalysis(null);
                setMetodoPago('transferencia');
                setSubmittedOrder(null);
                setStep(1);
                if (direction === 'bob_clp') {
                  setDisplayBob('1.000'); setDisplayClp('');
                  triggerAutoQuote('bob', '1.000');
                } else {
                  setDisplayClp('100.000'); setDisplayBob('');
                  triggerAutoQuote('clp', '100.000');
                }
              }}
              data-testid="emp-success-new-envio"
              className="flex-1 px-3 py-2.5 border-2 border-emerald-600 text-emerald-700 font-bold rounded-lg hover:bg-emerald-50 transition"
            >
              💸 Otro envío
            </button>
            <button
              onClick={() => {
                setSubmittedOrder(null);
                setStep(1);
                onCreated && onCreated(); // navega al historial
              }}
              data-testid="emp-success-go-historial"
              className="flex-1 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg transition"
            >
              📋 Ver historial
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-slate-700 mb-1 block">{label}</span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
    />
  </label>
);

// =============================================================
// Tab Historial
// =============================================================
const TabHistorial = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    empresaApi.get('/empresas/me/orders').then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-slate-500">Cargando historial…</div>;
  if (data.items.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <p className="text-slate-500">Aún no tienes envíos. ¡Cuando crees el primero aparecerá aquí!</p>
      </div>
    );
  }

  return (
    <>
      {/* Vista MÓVIL: tarjetas con botones grandes tocables */}
      <div className="md:hidden space-y-3" data-testid="emp-historial-list-mobile">
        {data.items.map((o) => (
          <OrderCardMobile key={o.id} order={o} />
        ))}
      </div>

      {/* Vista DESKTOP: tabla clásica */}
      <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="emp-historial-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left p-2">N°</th>
              <th className="text-left p-2">Fecha</th>
              <th className="text-right p-2">Envía</th>
              <th className="text-right p-2">Recibe</th>
              <th className="text-left p-2">Destinatario</th>
              <th className="text-center p-2">Estado</th>
              <th className="text-center p-2">Comprobantes</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((o) => (
              <tr key={o.id} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="p-2 font-mono text-xs">{o.short_no}</td>
                <td className="p-2 text-xs text-slate-600">{fmtFechaCl(o.created_at)}</td>
                <td className="p-2 text-right font-semibold">
                  {o.direction === 'bob_clp'
                    ? `Bs ${Math.round(o.amount_bob || 0).toLocaleString('es-BO')}`
                    : `$${(o.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`}
                </td>
                <td className="p-2 text-right font-semibold text-emerald-700">
                  {o.direction === 'bob_clp'
                    ? `$${Math.round(o.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`
                    : `Bs ${Math.round(o.amount_bob || 0).toLocaleString('es-BO')}`}
                </td>
                <td className="p-2 text-xs">
                  {o.recipient?.nombre} · {o.recipient?.banco}
                  {(o.recipient_qr || o.recipient?.payment_method === 'qr') && (
                    <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-bold border border-purple-200">📱 QR</span>
                  )}
                </td>
                <td className="p-2 text-center">
                  <span className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-semibold">{o.status}</span>
                </td>
                <td className="p-2 text-center whitespace-nowrap">
                  <OrderActions order={o} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Carlos jul 1, 2026 — Tarjeta mobile-first para historial en móvil
// Reemplaza la tabla en pantallas <md. Botones grandes 44px+ para tocar bien.
const OrderCardMobile = ({ order }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid={`emp-order-card-${order.id}`}>
    <div className="flex items-start justify-between mb-2">
      <div className="min-w-0">
        <p className="font-mono text-[11px] text-slate-500">{order.short_no}</p>
        <p className="text-[11px] text-slate-500">{fmtFechaCl(order.created_at)}</p>
      </div>
      <span className="shrink-0 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[10px] font-semibold border border-slate-200">
        {order.status}
      </span>
    </div>

    <div className="flex items-baseline justify-between mb-2">
      {order.direction === 'bob_clp' ? (
        <>
          <p className="text-lg font-black text-slate-900">Bs {Math.round(order.amount_bob || 0).toLocaleString('es-BO')} <span className="text-xs font-medium text-slate-500">BOB 🇧🇴</span></p>
          <p className="text-base font-black text-emerald-700">${Math.round(order.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} <span className="text-xs font-medium text-emerald-600">CLP 🇨🇱</span></p>
        </>
      ) : (
        <>
          <p className="text-lg font-black text-slate-900">${(order.amount_clp || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} <span className="text-xs font-medium text-slate-500">CLP</span></p>
          <p className="text-base font-black text-emerald-700">{Math.round(order.amount_bob || 0).toLocaleString('es-BO')} <span className="text-xs font-medium text-emerald-600">BOB</span></p>
        </>
      )}
    </div>

    <p className="text-xs text-slate-600 mb-3 truncate">
      → {order.recipient?.nombre || '—'} · {order.recipient?.banco || '—'}
      {(order.recipient_qr || order.recipient?.payment_method === 'qr') && (
        <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-bold border border-purple-200">📱 QR</span>
      )}
    </p>

    <OrderActions order={order} variant="mobile" />
  </div>
);


// Carlos jun 30, 2026 — botones de acción por orden (comprobante / DTE / pago final)
// Carlos jul 1, 2026 — reemplazamos window.open() por MODAL INLINE. Safari mobile
// bloquea window.open() cuando se llama después de un await (perdida de user gesture).
// El modal renderiza imagen inline o iframe para PDF, con anchor <a> nativo
// dentro para "Abrir en nueva pestaña" (click directo = permitido).
const OrderActions = ({ order, variant = 'desktop' }) => {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { text, tone: 'info'|'error' }
  const [modal, setModal] = useState(null); // { kind: 'image'|'pdf', src, title }

  const showToast = (text, tone = 'info') => {
    setToast({ text, tone });
    setTimeout(() => setToast(null), 4000);
  };

  const viewComprobante = async () => {
    try {
      setBusy(true);
      const r = await empresaApi.get(`/empresas/me/orders/${order.id}/comprobante`);
      const mime = r.data.mime || 'image/jpeg';
      const isPdf = mime === 'application/pdf' || mime.includes('pdf');
      setModal({
        kind: isPdf ? 'pdf' : 'image',
        src: `data:${mime};base64,${r.data.image_base64}`,
        title: `Comprobante depósito #${order.short_no}`,
      });
    } catch (e) {
      showToast(e.response?.data?.detail || 'No se pudo cargar el comprobante', 'error');
    } finally {
      setBusy(false);
    }
  };

  const viewPaymentProof = async () => {
    try {
      setBusy(true);
      const r = await empresaApi.get(`/empresas/me/orders/${order.id}/payment-proof`);
      if (r.data?.image_base64) {
        const mime = r.data.mime || 'image/jpeg';
        const isPdf = mime === 'application/pdf' || mime.includes('pdf');
        setModal({
          kind: isPdf ? 'pdf' : 'image',
          src: `data:${mime};base64,${r.data.image_base64}`,
          title: `Pago a destinatario #${order.short_no}`,
        });
      } else if (r.data?.url) {
        // URL legacy (payment_receipt_url) — no sabemos MIME, defaulteamos imagen
        setModal({ kind: 'image', src: r.data.url, title: `Pago a destinatario #${order.short_no}` });
      }
    } catch (e) {
      showToast(e.response?.data?.detail || 'Aún no hay comprobante de pago al destinatario', 'info');
    } finally {
      setBusy(false);
    }
  };

  const downloadDte = async () => {
    try {
      setBusy(true);
      const r = await empresaApi.get(`/empresas/me/orders/${order.id}/dte`);
      if (r.data?.pdf_url) {
        setModal({ kind: 'pdf', src: r.data.pdf_url, title: `Boleta DTE #${order.short_no}` });
      }
    } catch (e) {
      showToast(e.response?.data?.detail || 'Boleta DTE aún no emitida', 'info');
    } finally {
      setBusy(false);
    }
  };

  // Carlos jul 2, 2026 — ver el QR del destinatario (órdenes creadas con QR)
  const hasQr = !!(order.recipient_qr || order.recipient?.payment_method === 'qr');
  const viewRecipientQr = async () => {
    try {
      setBusy(true);
      const r = await empresaApi.get(`/empresas/me/orders/${order.id}/recipient-qr`);
      const mime = r.data.mime || 'image/jpeg';
      setModal({
        kind: 'image',
        src: `data:${mime};base64,${r.data.image_base64}`,
        title: `QR destinatario #${order.short_no}`,
      });
    } catch (e) {
      showToast(e.response?.data?.detail || 'Esta orden no tiene QR', 'info');
    } finally {
      setBusy(false);
    }
  };

  // Estilo botones: mobile = pill grande tappable (min 44px), desktop = link compacto
  const btnClass = variant === 'mobile'
    ? 'flex-1 min-h-[44px] inline-flex items-center justify-center px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 text-emerald-800 font-semibold text-xs rounded-lg border border-emerald-200 disabled:opacity-50 transition'
    : 'text-xs text-emerald-700 hover:underline disabled:opacity-50';

  return (
    <>
      <div className={`relative ${variant === 'mobile' ? 'flex gap-2 flex-wrap' : 'flex gap-2 justify-center'}`}>
        {toast && (
          <div
            data-testid={`order-action-toast-${order.id}`}
            className={`absolute ${variant === 'mobile' ? '-top-10' : '-top-12'} left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
              toast.tone === 'error'
                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}
          >
            {toast.text}
          </div>
        )}
        <button
          type="button"
          onClick={viewComprobante}
          disabled={busy}
          title="Mi comprobante de depósito"
          data-testid={`emp-comp-mine-${order.id}`}
          className={btnClass}
        >
          📄 Mi pago
        </button>
        {variant === 'desktop' && <span className="text-slate-300">·</span>}
        <button
          type="button"
          onClick={viewPaymentProof}
          disabled={busy}
          title={`Comprobante ${BRAND.name} → destinatario`}
          data-testid={`emp-comp-paid-${order.id}`}
          className={btnClass}
        >
          🇧🇴 Pago final
        </button>
        {variant === 'desktop' && order.direction !== 'bob_clp' && <span className="text-slate-300">·</span>}
        {order.direction !== 'bob_clp' && (
          <button
            type="button"
            onClick={downloadDte}
            disabled={busy}
            title="Boleta legal DTE (PDF)"
            data-testid={`emp-dte-${order.id}`}
            className={btnClass}
          >
            🧾 Boleta PDF
          </button>
        )}
        {hasQr && (
          <>
            {variant === 'desktop' && <span className="text-slate-300">·</span>}
            <button
              type="button"
              onClick={viewRecipientQr}
              disabled={busy}
              title="QR de pago del destinatario"
              data-testid={`emp-qr-view-${order.id}`}
              className={variant === 'mobile'
                ? 'flex-1 min-h-[44px] inline-flex items-center justify-center px-3 py-2.5 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 text-purple-800 font-semibold text-xs rounded-lg border border-purple-200 disabled:opacity-50 transition'
                : 'text-xs text-purple-700 hover:underline disabled:opacity-50'}
            >
              📱 Ver QR
            </button>
          </>
        )}
      </div>

      {modal && (
        <MediaModal
          open
          onClose={() => setModal(null)}
          kind={modal.kind}
          src={modal.src}
          title={modal.title}
        />
      )}
    </>
  );
};

// Carlos jul 1, 2026 — Modal fullscreen para imagen o PDF. Reemplaza window.open()
// que Safari/Chrome mobile bloqueaban como popup. Incluye <a> nativo para
// descargar/abrir en pestaña nueva (el click dentro del modal es user gesture
// fresco → permitido en todos los navegadores).
//
// Nota crítica: los browsers modernos (Chrome/Edge/Safari) BLOQUEAN iframes con
// `src="data:application/pdf;base64,..."` por seguridad. Convertimos el data URL
// a Blob URL para que el iframe renderice el PDF sin ser bloqueado.
const MediaModal = ({ open, onClose, kind, src, title }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!open || kind !== 'pdf' || !src?.startsWith('data:')) {
      setBlobUrl(null);
      return;
    }
    // Convertir data URL base64 → Blob URL para iframe
    try {
      const [head, b64] = src.split(',', 2);
      const mime = head.split(':', 2)[1].split(';', 1)[0] || 'application/pdf';
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Blob URL conversion failed', err);
      setBlobUrl(null);
    }
  }, [open, kind, src]);

  if (!open) return null;

  // Para iframe usamos blob URL si el src es data URL. Si es http URL directa,
  // usamos src tal cual. El anchor <a> siempre usa blobUrl (mejor UX descarga).
  const iframeSrc = kind === 'pdf' ? (blobUrl || src) : src;
  const anchorHref = kind === 'pdf' ? (blobUrl || src) : src;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
      onClick={onClose}
      data-testid="emp-media-modal"
    >
      <div className="flex items-center justify-between gap-2 p-3 bg-black/40 backdrop-blur">
        <span className="text-sm font-semibold text-white truncate">{title}</span>
        <div className="flex gap-2 shrink-0">
          <a
            href={anchorHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid="emp-media-modal-open-new"
            className="min-h-[40px] inline-flex items-center px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg border border-white/30"
          >
            ↗ Abrir / Descargar
          </a>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="emp-media-modal-close"
            className="min-h-[40px] inline-flex items-center px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg border border-white/30"
          >
            ✕ Cerrar
          </button>
        </div>
      </div>
      <div
        className="flex-1 overflow-auto p-2 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === 'image' ? (
          <img
            src={src}
            alt={title}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
        ) : iframeSrc ? (
          <iframe
            src={iframeSrc}
            title={title}
            className="w-full h-full min-h-[75vh] bg-white rounded-lg"
          />
        ) : (
          <p className="text-white/70 text-sm">Preparando visor…</p>
        )}
      </div>
    </div>
  );
};

// =============================================================
// Tab Perfil
// =============================================================
const TabPerfil = ({ me }) => (
  <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7">
    <h2 className="text-xl font-black text-slate-900 mb-4">🏢 Perfil de la empresa</h2>
    <div className="space-y-2 text-sm">
      <Row k="Razón social" v={me.razon_social} />
      <Row k="RUT" v={me.rut} />
      <Row k="Representante legal" v={me.rep_legal_name} />
      <Row k="Email" v={me.email} />
      <Row k="Teléfono" v={me.phone} />
      <Row k="Estado" v={<span className="font-semibold text-emerald-700">✅ Aprobada</span>} />
      <Row k="Aprobada el" v={me.approved_at?.slice(0, 16).replace('T', ' ')} />
    </div>
    <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap gap-3">
      <Link to="/empresas/docs" className="text-sm text-emerald-700 hover:underline" data-testid="emp-go-docs">
        📂 Ver / actualizar documentos
      </Link>
      <span className="text-slate-300">·</span>
      <ChangePasswordForm />
    </div>
  </div>
);


// Carlos jun 30, 2026 — Cambio de contraseña para empresas
const ChangePasswordForm = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    setErr(null);
    if (newPwd.length < 8) {
      setErr('La nueva contraseña debe tener mínimo 8 caracteres');
      return;
    }
    if (newPwd !== confirm) {
      setErr('Las contraseñas no coinciden');
      return;
    }
    try {
      setBusy(true);
      await empresaApi.post('/empresas/me/change-password', {
        current_password: current,
        new_password: newPwd,
      });
      setMsg('✅ Contraseña actualizada');
      setCurrent('');
      setNewPwd('');
      setConfirm('');
      setTimeout(() => setOpen(false), 1500);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="emp-open-change-password"
        className="text-sm text-emerald-700 hover:underline"
      >
        🔐 Cambiar contraseña
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="w-full mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="emp-change-password-form">
      <h3 className="font-bold text-slate-900 text-sm">🔐 Cambiar contraseña</h3>
      <input
        type="password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        placeholder="Contraseña actual"
        autoComplete="current-password"
        required
        data-testid="emp-pwd-current"
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
      <input
        type="password"
        value={newPwd}
        onChange={(e) => setNewPwd(e.target.value)}
        placeholder="Nueva contraseña (mín. 8 caracteres)"
        autoComplete="new-password"
        required
        data-testid="emp-pwd-new"
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
      <input
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Confirma nueva contraseña"
        autoComplete="new-password"
        required
        data-testid="emp-pwd-confirm"
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />
      {err && <p className="text-red-600 text-xs" data-testid="emp-pwd-err">{err}</p>}
      {msg && <p className="text-emerald-700 text-xs" data-testid="emp-pwd-ok">{msg}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="emp-pwd-submit"
          className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setErr(null); setMsg(null); }}
          data-testid="emp-pwd-cancel"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-100"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500 text-xs">{k}</span>
    <span className="text-slate-900 font-medium text-right">{v || '—'}</span>
  </div>
);

export default EmpresaPanel;
